import React, { useEffect, useState } from "react";
import { Smartphone, X, Share2, Plus, MoreVertical } from "lucide-react";

const DISMISS_KEY = "stk_pwa_dismiss_until";

function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)").matches
    || window.navigator?.standalone === true;
}

function isIos() {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent || "") && !window.MSStream;
}

function isAndroid() {
  if (typeof navigator === "undefined") return false;
  return /android/i.test(navigator.userAgent || "");
}

function getDismissUntil() {
  try {
    const v = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    return Number.isFinite(v) ? v : 0;
  } catch { return 0; }
}

function dismissFor(days) {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + days * 86400_000));
  } catch { /* ignored */ }
}

export default function InstallPWA() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);
  const [androidHint, setAndroidHint] = useState(false);

  // Register service worker
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      window.addEventListener("load", () => {
        navigator.serviceWorker.register("/sw.js").catch((e) => console.warn("SW reg failed:", e));
      });
    }
  }, []);

  useEffect(() => {
    if (isStandalone()) return;
    if (getDismissUntil() > Date.now()) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      // Delay to avoid being annoying on first paint
      setTimeout(() => setShow(true), 4000);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS doesn't fire beforeinstallprompt; show a hint after delay
    if (isIos()) {
      setTimeout(() => setShow(true), 6000);
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  if (!show) return null;
  if (isStandalone()) return null;

  const install = async () => {
    if (deferred) {
      try {
        deferred.prompt();
        const { outcome } = await deferred.userChoice;
        if (outcome === "accepted") {
          setShow(false);
        } else {
          dismissFor(7);
          setShow(false);
        }
        setDeferred(null);
      } catch (e) {
        console.error(e);
      }
    } else if (isIos()) {
      setIosHint(true);
    } else {
      // Android Chrome without auto-prompt (e.g. dismissed earlier) or other browser
      setAndroidHint(true);
    }
  };

  const close = () => {
    dismissFor(14);
    setShow(false);
  };

  return (
    <>
      <div
        data-testid="pwa-install-card"
        className="fixed left-3 right-3 bottom-3 sm:left-auto sm:right-6 sm:bottom-6 sm:w-[360px] z-[65] rounded-2xl border border-amber-300/30 bg-[#0A0D17]/95 backdrop-blur-xl p-4 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.6)] text-slate-200"
      >
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-300/10 border border-amber-300/30 text-amber-300">
            <Smartphone className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className="font-heading text-sm font-bold text-white">
              Přidejte si STK Komfort do telefonu
            </div>
            <p className="mt-1 text-xs text-slate-400 leading-relaxed">
              Funguje jako webová aplikace — žádný App Store ani Google Play.
              Stačí jedním klikem přidat na plochu telefonu pro rychlý přístup
              k objednávkám, QR platbě a chatu s Lenkou.
            </p>
          </div>
          <button type="button" onClick={close} data-testid="pwa-close" aria-label="Zavřít" className="text-slate-500 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={install}
            data-testid="pwa-install-btn"
            className="flex-1 rounded-lg bg-gradient-to-b from-amber-300 to-amber-500 hover:from-amber-200 hover:to-amber-400 px-3 py-2.5 text-sm font-bold text-slate-900"
          >
            Přidat na plochu
          </button>
        </div>
      </div>

      {iosHint && (
        <div
          data-testid="pwa-ios-hint"
          className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-4 bg-black/70"
          onClick={() => setIosHint(false)}
        >
          <div className="w-full max-w-md rounded-2xl bg-[#0A0D17] border border-amber-300/25 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-300/10 border border-amber-300/30 text-amber-300">
                <Plus className="h-5 w-5" />
              </div>
              <div className="font-heading text-lg font-bold text-white">Přidat na plochu — iPhone / iPad</div>
            </div>
            <ol className="mt-4 space-y-2 text-sm text-slate-300 leading-relaxed list-decimal list-inside">
              <li>Otevřete <strong className="text-white">stkkomfort.cz</strong> v prohlížeči <strong className="text-white">Safari</strong>.</li>
              <li>Klepněte dole na ikonu <Share2 className="inline h-4 w-4 align-text-bottom text-amber-300" /> <strong className="text-white">Sdílet</strong>.</li>
              <li>Zvolte <strong className="text-white">Přidat na plochu</strong> a potvrďte tlačítkem <strong className="text-white">Přidat</strong>.</li>
            </ol>
            <p className="mt-3 text-xs text-slate-500">
              STK Komfort není publikovaný v App Store — funguje přímo jako webová aplikace přidaná na plochu.
            </p>
            <button type="button" onClick={() => setIosHint(false)} className="mt-5 w-full rounded-lg bg-amber-300 text-slate-900 font-bold py-2.5 text-sm">
              Rozumím
            </button>
          </div>
        </div>
      )}

      {androidHint && (
        <div
          data-testid="pwa-android-hint"
          className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-4 bg-black/70"
          onClick={() => setAndroidHint(false)}
        >
          <div className="w-full max-w-md rounded-2xl bg-[#0A0D17] border border-amber-300/25 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-300/10 border border-amber-300/30 text-amber-300">
                <MoreVertical className="h-5 w-5" />
              </div>
              <div className="font-heading text-lg font-bold text-white">Přidat na plochu — Android / Chrome</div>
            </div>
            <ol className="mt-4 space-y-2 text-sm text-slate-300 leading-relaxed list-decimal list-inside">
              <li>Otevřete <strong className="text-white">stkkomfort.cz</strong> v prohlížeči <strong className="text-white">Chrome</strong>.</li>
              <li>Klepněte na <MoreVertical className="inline h-4 w-4 align-text-bottom text-amber-300" /> <strong className="text-white">tři tečky</strong> vpravo nahoře.</li>
              <li>Zvolte <strong className="text-white">Nainstalovat aplikaci</strong> nebo <strong className="text-white">Přidat na plochu</strong> a potvrďte.</li>
            </ol>
            <p className="mt-3 text-xs text-slate-500">
              STK Komfort není publikovaný v Google Play — funguje přímo jako webová aplikace přidaná na plochu telefonu.
            </p>
            <button type="button" onClick={() => setAndroidHint(false)} className="mt-5 w-full rounded-lg bg-amber-300 text-slate-900 font-bold py-2.5 text-sm">
              Rozumím
            </button>
          </div>
        </div>
      )}
    </>
  );
}
